import { ReactElement, useMemo } from 'react';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { DataMode, buildRatioForceScatter, maxXValue } from './auxSensorChannels';
import {
  AUX_COLORS,
  AuxPlotSeriesSpec,
  DEFAULT_PAIR_LABEL,
  RATIO_ONE_ANNOTATION,
  auxBaseOptions,
  lineSpec,
  ratioYAxis,
  scatterSpec,
  styledApexSeries,
  xAxisLabel,
} from './auxPlotStyle';
import AuxFitControls from './AuxFitControls';
import AuxPlotCard from './AuxPlotCard';
import { useScatterFit } from './useScatterFit';

interface AuxRatioForcePlotProps {
  gridded: Record<string, (number | null)[]> | undefined;
  circumference: number;
  /** Min linear speed (m/s) — samples below are masked (shared slider). */
  threshold: number;
  dataMode?: DataMode;
  isLoading?: boolean;
  /** Names the two wheel sensors the ratio is between — see AuxRatioTimePlot. */
  pairLabel?: string;
}

const CHART_HEIGHT = 380;

/**
 * Slip-vs-tractive-force scatter for the two wheel sensors. Y is the slip ratio
 * (wheel speed 1 / wheel speed 2), X is the tyre driving force (rider power /
 * ground speed, in N). Low-speed samples — where force/ratio blow up — are masked
 * by the shared min-speed threshold and drawn in light grey rather than hidden.
 * A dashed line marks ratio = 1 (agreement).
 *
 * The "Show fit" checkbox adds a least-squares fit of ratio against force
 * (slope / offset / R² printed above the chart, fit line drawn over the points)
 * together with a two-thumb min/max driving-force window; samples outside that
 * window are excluded from the fit and drawn in mid grey.
 */
export default function AuxRatioForcePlot({
  gridded,
  circumference,
  threshold,
  dataMode = 'all',
  isLoading = false,
  pairLabel = DEFAULT_PAIR_LABEL,
}: AuxRatioForcePlotProps): ReactElement {
  const { included: points, excluded: masked } = useMemo(
    () => buildRatioForceScatter(gridded, circumference, threshold, dataMode),
    [gridded, circumference, threshold, dataMode],
  );

  const maxForce = useMemo(() => maxXValue(points), [points]);
  const seriesPoints = useMemo(() => [points], [points]);
  const scatterFit = useScatterFit(seriesPoints, maxForce, 'ratioForce');
  const { included, excluded, fitLine, fit } = scatterFit.results[0];

  const specs = useMemo<AuxPlotSeriesSpec[]>(() => {
    const out = [scatterSpec(pairLabel, included, AUX_COLORS.ratio)];
    if (excluded.length) out.push(scatterSpec('Outside fit window', excluded, AUX_COLORS.outsideFit));
    if (masked.length) {
      out.push(scatterSpec(`Below ${threshold.toFixed(1)} m/s`, masked, AUX_COLORS.masked));
    }
    if (fitLine) out.push(lineSpec('Fit', fitLine, AUX_COLORS.fit));
    return out;
  }, [included, excluded, masked, threshold, fitLine, pairLabel]);

  const { series: chartSeries, options: styleOptions } = useMemo(() => styledApexSeries(specs), [specs]);

  const options = useMemo<ApexOptions>(() => {
    const base = auxBaseOptions(CHART_HEIGHT);
    return {
      ...base,
      ...styleOptions,
      chart: { ...base.chart, type: 'line' },
      legend: { ...base.legend, show: specs.length > 1 },
      xaxis: {
        type: 'numeric',
        tickAmount: 8,
        title: { text: 'Tyre Driving Force (N)', style: { fontSize: '13px', fontWeight: 600 } },
        // Same zoom-following precision as the time card's x-axis, so the two
        // slip-ratio plots do not print a tick row one of them cannot resolve.
        labels: { formatter: xAxisLabel() },
      },
      yaxis: ratioYAxis(pairLabel),
      annotations: RATIO_ONE_ANNOTATION,
      tooltip: {
        shared: false,
        intersect: true,
        x: { formatter: (value: number) => `${value.toFixed(1)} N` },
        y: { formatter: (value: number) => (value != null ? value.toFixed(3) : '') },
      },
    };
  }, [styleOptions, specs.length, pairLabel]);

  return (
    <AuxPlotCard
      title="Slip Ratio vs Tyre Driving Force"
      isLoading={isLoading}
      isEmpty={points.length === 0 && masked.length === 0}
      emptyMessage="No slip-ratio samples for this run (needs two wheel sensors + power)."
      controls={!isLoading && points.length > 0 ? (
        <AuxFitControls
          showFit={scatterFit.showFit}
          onToggleFit={scatterFit.setShowFit}
          window={scatterFit.window}
          onWindowChange={scatterFit.setWindow}
          sliderLabel="Driving force window"
          sliderUnit="N"
          sliderMax={maxForce}
          readouts={[{ fit }]}
          format={{ slopeUnit: '/N', slopeDigits: 6, offsetDigits: 4 }}
        />
      ) : null}
    >
      <ReactApexChart options={options} series={chartSeries} type="line" height={CHART_HEIGHT} />
    </AuxPlotCard>
  );
}
